import { RiFlagLine } from '@remixicon/react';

import { ButtonIcon, IconExternalLink } from '@spotify-internal/encore-web';
import { pathToModuleStyle } from '@spotify/confidence-core-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';
import {
  DefaultArtifactListItem,
  createArtifactExtension,
  getArtifactNavigationPath,
  isExternalLink,
} from '@spotify/plugin-confidence-projects-react';

import { projectsPlugin } from '../plugin';
import { ResourcePreview } from './previews/ResourcePreview';
import {
  RuleArtifactListItem,
  SegmentArtifactListItem,
  WorkflowArtifactListItem,
} from './resolvers';
import { isRuleUri, isSegmentUri, isWorkflowInstanceUri } from './resourceUri';

const GoToResourceButton = ({ artifact }: { artifact: AttachmentFragment }) => {
  const navigationPath = getArtifactNavigationPath(artifact.uri);

  if (!navigationPath) {
    return null;
  }

  return (
    <ButtonIcon
      variant="tertiary"
      onClick={() => window.open(`/${navigationPath}`, '_blank')}
      aria-label="Go to resource"
     data-track-source-anchor="29aebb0b3c;;low">
      <IconExternalLink  data-track-source-anchor="a0bb90b328;go-to-resource-button-icon-external-link"/>
    </ButtonIcon>
  );
};

export const ResourceArtifactExtension = projectsPlugin.provide(
  createArtifactExtension({
    id: 'resource',
    predicate: artifact => !isExternalLink(artifact.uri),
    icon: artifact => {
      const style = pathToModuleStyle(artifact.uri);
      if (style) {
        return {
          Icon: style.Icon,
          color: style.color,
        };
      }
      return { Icon: RiFlagLine, color: '#E74C3C' };
    },
    label: 'Resource',
    category: 'Resources',
    getNavigationPath: artifact => getArtifactNavigationPath(artifact.uri),
    render: artifact => <ResourcePreview artifact={artifact} />,
    renderHeaderActions: artifact => <GoToResourceButton artifact={artifact}  data-track-source-anchor="f93355fca9;resource-artifact-extension-go-to-resource"/>,
    renderListItem: (
      artifact,
      { onSelect, onNavigateToResource, onDelete, icon },
    ) => {
      if (isWorkflowInstanceUri(artifact.uri)) {
        return (
          <WorkflowArtifactListItem
            artifact={artifact}
            icon={icon}
            onSelect={onSelect}
            onNavigateToResource={onNavigateToResource}
            onDelete={onDelete}
          />
        );
      }
      if (isSegmentUri(artifact.uri)) {
        return (
          <SegmentArtifactListItem
            artifact={artifact}
            icon={icon}
            onSelect={onSelect}
            onNavigateToResource={onNavigateToResource}
            onDelete={onDelete}
          />
        );
      }
      if (isRuleUri(artifact.uri)) {
        return (
          <RuleArtifactListItem
            artifact={artifact}
            icon={icon}
            onSelect={onSelect}
            onNavigateToResource={onNavigateToResource}
            onDelete={onDelete}
          />
        );
      }
      const navigationPath = getArtifactNavigationPath(artifact.uri);
      const href = navigationPath ? `/${navigationPath}` : undefined;
      return (
        <DefaultArtifactListItem
          artifact={artifact}
          icon={icon}
          href={href}
          onSelect={onSelect}
          onDelete={onDelete}
         data-track-source-anchor="6c5845fad7;resource-artifact-extension-default-artifact"/>
      );
    },
  }),
);
